// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

/**
 * Reusable skeleton loader that replaces CircularProgress spinners.
 * Renders layout-matching placeholders for a more premium loading experience.
 *
 * Variants:
 *   - "table"   : Table-like rows (default 5 rows)
 *   - "list"    : List items with avatar + text
 *   - "card"    : Card-like blocks in a grid
 *   - "text"    : Paragraph text lines
 *   - "metric"  : Dashboard metric cards
 */
const ContentSkeleton = ({ variant = 'table', rows = 5, columns = 3 }) => {
  if (variant === 'table') {
    return (
      <Box sx={{ py: 1 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, px: 1 }}>
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={14}
              sx={{ flex: i === 0 ? 2 : 1, borderRadius: '4px' }}
              animation="wave"
            />
          ))}
        </Box>
        {/* Data rows */}
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <Box
            key={rowIdx}
            sx={{
              display: 'flex',
              gap: 2,
              py: 1.2,
              px: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {Array.from({ length: columns }).map((_, colIdx) => (
              <Skeleton
                key={colIdx}
                variant="rounded"
                height={12}
                sx={{
                  flex: colIdx === 0 ? 2 : 1,
                  borderRadius: '3px',
                  opacity: 0.7 - rowIdx * 0.08,
                }}
                animation="wave"
              />
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  if (variant === 'list') {
    return (
      <Box sx={{ py: 1 }}>
        {Array.from({ length: rows }).map((_, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1.2,
              px: 1,
            }}
          >
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              animation="wave"
              sx={{ flexShrink: 0 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton
                variant="rounded"
                height={13}
                width={`${60 + Math.random() * 30}%`}
                sx={{ borderRadius: '3px', mb: 0.8 }}
                animation="wave"
              />
              <Skeleton
                variant="rounded"
                height={10}
                width={`${30 + Math.random() * 25}%`}
                sx={{ borderRadius: '3px', opacity: 0.6 }}
                animation="wave"
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (variant === 'card') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 2,
          py: 1,
        }}
      >
        {Array.from({ length: rows }).map((_, i) => (
          <Box
            key={i}
            sx={{
              p: 2,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Skeleton
              variant="rounded"
              height={14}
              width="60%"
              sx={{ borderRadius: '3px', mb: 1.5 }}
              animation="wave"
            />
            <Skeleton
              variant="rounded"
              height={28}
              width="40%"
              sx={{ borderRadius: '4px', mb: 1 }}
              animation="wave"
            />
            <Skeleton
              variant="rounded"
              height={10}
              width="80%"
              sx={{ borderRadius: '3px', opacity: 0.5 }}
              animation="wave"
            />
          </Box>
        ))}
      </Box>
    );
  }

  if (variant === 'metric') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 2,
          py: 1,
        }}
      >
        {Array.from({ length: rows }).map((_, i) => (
          <Box key={i} sx={{ p: 1.5 }}>
            <Skeleton
              variant="rounded"
              height={11}
              width="50%"
              sx={{ borderRadius: '3px', mb: 1 }}
              animation="wave"
            />
            <Skeleton
              variant="rounded"
              height={24}
              width="35%"
              sx={{ borderRadius: '4px' }}
              animation="wave"
            />
          </Box>
        ))}
      </Box>
    );
  }

  // Default: text variant
  return (
    <Box sx={{ py: 1, px: 1 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={12}
          width={`${60 + Math.random() * 35}%`}
          sx={{
            borderRadius: '3px',
            mb: 1.2,
            opacity: 0.8 - i * 0.1,
          }}
          animation="wave"
        />
      ))}
    </Box>
  );
};

export default ContentSkeleton;
